import {EventEmitter2} from 'eventemitter2';
import {injectable} from "inversify";
import {app} from "@/di";
import {nonblocking} from "@/utils";

export abstract class AbstractEvent {
  public static getEventName(): string {
    return this.name;
  }
}

export interface EventSubscriber {
  init(): { event: HasEventName, callback: (event: AbstractEvent) => void }[];

  // higher priority will be executed first
  priority(): number;
}

export type HasEventName = {
  getEventName(): string,
}

@injectable()
export class EventEmitter {
  em: EventEmitter2;
  private eventStack: { [key: string]: AbstractEvent[] } = {};

  constructor() {
    this.em = new EventEmitter2();
    this.em.setMaxListeners(100);
  }

  init() {
    collect(app.getAll<EventSubscriber>("EventSubscriber"))
      .sortByDesc((s: EventSubscriber) => s.priority())
      .each((s: EventSubscriber) => this.addSubscriber(s));
  }

  onOff(event: HasEventName | HasEventName[], callback: (event: AbstractEvent) => void) {
    this.on(event, callback);

    return () => this.off(event, callback);
  }

  once(event: HasEventName | HasEventName[], callback: (event: AbstractEvent) => void) {
    if (!Array.isArray(event)) {
      event = [event];
    }

    event.forEach(e => this.em.once(e.getEventName(), callback));
  }

  on(event: HasEventName | HasEventName[], callback: (event: AbstractEvent) => void) {
    if (!Array.isArray(event)) {
      event = [event];
    }

    event.forEach(e => this.em.on(e.getEventName(), callback));
  }

  off(event: HasEventName | HasEventName[], callback: (event: AbstractEvent) => void) {
    if (!Array.isArray(event)) {
      event = [event];
    }

    event.forEach(e => this.em.off(e.getEventName(), callback));
  }

  addSubscriber(subscriber: EventSubscriber) {
    subscriber.init().forEach(({event, callback}) => {
      this.on(event, event => callback.call(subscriber, event));
    });
  }

  async emitAsync(event: HasEventName | AbstractEvent) {
    console.debug(event);
    // @ts-ignore
    console.time(event.constructor.getEventName());

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    for (const listener of this.em.listeners(event.constructor.getEventName())) {
      await nonblocking(async () => await listener(event));
    }

    // @ts-ignore
    console.timeEnd(event.constructor.getEventName());
  }

  stack(stackName: string, event: HasEventName | AbstractEvent) {
    this.eventStack[stackName] ??= [];
    this.eventStack[stackName].push(event);
  }

  async flushAsync(name: string) {
    await (this.eventStack[name] || []).forEachAsync((e: AbstractEvent) => this.emitAsync(e));
    delete this.eventStack[name];
  }
}
